import { Button, Form, Popover, Tag, Typography } from "antd";
import _, { filter, some } from "lodash";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import DashboardAPI from "restAPI/DashboardAPI";
import { LoadingIcon } from "./Icons";
import Vocabulary, { getVocabulary } from "./Vocabulary";
import ArmorCodeGroupSelectionList from "./ArmorCodeGroupSelectionList";
import ArmorCodeGroupSelectionListV2 from "./ArmorCodeGroupSelectionList_V2";
import { filterGroupsByGroupsFilter } from "utils/armorcodeGroupUtil";
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";

const { Text } = Typography;
const CHIP_GAP = 8;

const ArmorCodeGroupField = forwardRef(function ArmorCodeGroupField(
  { value, onChange, btnProps, disabled, applyGlobalFilter = true, allowGroupsFetch = true, groups = null, newVersion = false, showSelectAllActions = true, isCustomDashboardConfig = false },
  ref
) {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [visibleTillIndex, setVisibleTillIndex] = useState()
  const [allGroups, setAllGroups] = useState(groups);
  const [resetGroups, setResetGroups] = useState(false);
  const convertOldValueRef = useRef(true);
  const containerRef = useRef();
  const remainingCountElmentRef = useRef();

  useImperativeHandle(
    ref,
    () => ({
      getAllGroups: () => allGroups,
    }),
    [allGroups]
  );

  useEffect(() => {
    if(!allowGroupsFetch){
      setAllGroups(groups)
    }
  }, [groups, allowGroupsFetch])

  useEffect(() => {
    if (!allowGroupsFetch) {
      return;
    }
    const { promise, cancel } = applyGlobalFilter
      ? DashboardAPI.getAllFilteredGroups()
      : DashboardAPI.getAllGroups();
    promise.then((r) => setAllGroups(r.groups || []));
    return cancel;
  }, [applyGlobalFilter, allowGroupsFetch]);

  useEffect(() => {
    // Handle old widgets with hierarchy chosen from the previous version
    //since old version uses /.* when the parent hierarchy is chosen
    const hasOldGroupValue = some(value, (hierarchy) =>
      hierarchy.endsWith("/.*"),
    );

    if (newVersion && convertOldValueRef.current && hasOldGroupValue && allGroups) {
      // Convert old hierarchies and update the selected values:
      // The first spread includes all child hierarchies
      // The second spread includes parent hierarchies without the "/.*" suffix
      onChange([
        ...filterGroupsByGroupsFilter(allGroups, value),
        ...filter(value, (hierarchy) => hierarchy.endsWith("/.*")).map(
          (i) => i.split("/.*")[0],
        ),
      ]);
      convertOldValueRef.current = false;
    }
  }, [value, allGroups, onChange, newVersion]);

  useEffect(() => {
    setVisibleTillIndex(undefined);
    setResetGroups(true);
  }, [value]);

  useEffect(() => {
    if (resetGroups === true) {
      setResetGroups(false);
    }
  }, [resetGroups]);

  useLayoutEffect(() => {
    if (containerRef.current && !resetGroups) {
      const containerWidth = containerRef.current.getBoundingClientRect().width;
      const children = containerRef.current.children

      let sumWidth = 0
      for (let i = 0; i < children.length; i++) {
        const childWidth = children[i].getBoundingClientRect().width
        sumWidth += childWidth + CHIP_GAP;
        if (sumWidth >= containerWidth) {
          // This is to adjusting remaning count element itself to avoid getting cut off
          // in case where the last element before the remaning count element
          // has width end close to the parent width end
          if (remainingCountElmentRef.current === children[i]) {
            setVisibleTillIndex(i - 1);
          } else {
            setVisibleTillIndex(i);
          }
          return
        }
      }
    }
  }, [value, visibleTillIndex, resetGroups]);

  const visibleGroup = value?.slice(0, visibleTillIndex) ?? [];
  const reaminingCount = value?.length - visibleGroup.length;

  const listProps = {
    disabled,
    applyGlobalFilter,
    onChange,
    value,
    visible: popoverVisible,
    allGroups,
    showSelectAllActions
  };

  const onGroupRemove = (group) => {
    const selectedKeysAfterRemoval = _.filter(value, (val) => val !== group);
    onChange(selectedKeysAfterRemoval);
  };

  const newContent = (
    <div
      className={
        "column-filter column-filter-width column-filter-group new-version"
      }
      style={{ boxShadow: "none", width: 356, padding: 8 }}
    >
      <div className="column-filter-title">
        Select <Vocabulary name="group" />
      </div>

      <ArmorCodeGroupSelectionListV2 {...listProps} />
      {!_.isEmpty(value) ? (
        <div className="column-filter-group-content">
          <div className="column-filter-selected-value-section">
            <div className="column-filter-sub-title">
              Selected <Vocabulary name="group" />
            </div>
            <div
              className="column-filter-selected-value-wrapper d-flex flex-wrap"
              style={{ gap: 6, overflow: "auto", maxHeight: 260 }}
            >
              {value.map((val, i) => (
                <GroupChip
                  key={i}
                  className={"column-filter-selected-value m-0 d-flex align-items-center text-ellipsis"}
                  group={val}
                  onRemove={onGroupRemove}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
  
  const content = newVersion ? newContent : <ArmorCodeGroupSelectionList {...listProps} />;
  const { style, ...restButtonProps } = btnProps || {};
  const noGroupSelectedPlaceholder = isCustomDashboardConfig ? `All ${getVocabulary("group")} Parents` : `Selected ${getVocabulary("group", true)}`;

  return (
    <Popover
      placement="bottomLeft"
      content={content}
      trigger={"click"}
      overlayClassName="ant-popover-hide-arrow"
      onVisibleChange={setPopoverVisible}
      visible={popoverVisible}
    >
      <Button block {...restButtonProps} disabled={disabled} style={{ paddingInline: 10, ...style, }}>
        <div className="d-flex">
          {allGroups === null ? <LoadingIcon className="m-r-sm" /> : null}
          {_.isEmpty(value) ? (
            <Text className="text-ellipsis" type="secondary" title={noGroupSelectedPlaceholder}>
              {noGroupSelectedPlaceholder}
            </Text>
          ) : (
            !newVersion ? 
              <span className="text-ellipsis">{value.length} <Vocabulary name="group" isPlural={!(value.length === 1)} /> selected</span>
              :
              <>
                <div ref={containerRef} className="d-flex w-100" style={{whiteSpace: 'nowrap'}}>
                  {visibleGroup.map((group) => {
                    return (
                      <GroupChip
                        key={group}
                        className={'d-flex align-items-center'}
                        group={group}
                        onRemove={onGroupRemove}
                        style={{ maxWidth: 200 }}
                      />
                    )
                  })}
                  {reaminingCount > 0 ? (
                    <span
                      ref={remainingCountElmentRef}
                      className="text-small-highlight"
                      style={{
                        color: '#70737B',
                        display: 'inline-block',
                      }}
                    >
                      +{reaminingCount}
                    </span>
                  ) : null}
                </div>
              </>
          )}
        </div>
      </Button>
    </Popover>
  );
});

export default ArmorCodeGroupField;

export const ArmorCodeGroupFieldWithFormItem = forwardRef(function ArmorCodeGroupFieldWithFormItem(
  { name: formItemName = "group", ...props },
  ref
) {
  return (
    <Form.Item
      name={formItemName}
      rules={[
        {
          required: true,
          message: (
            <>
              <Vocabulary name="group" /> is required
            </>
          ),
        },
      ]}
    >
      <ArmorCodeGroupField {...props} ref={ref} />
    </Form.Item>
  );
});

const GroupChip = ({ className, group, onRemove, style }) => {
  return (
    <Tag
      className={className}
      style={{ fontSize: 13.75, ...style }}
      closable
      onClose={() => onRemove(group)}
    >
      <ACMiddleEllipsis text={group} />
    </Tag>
  )
}
